import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Users, FileText, Printer, Wand2, Download, Calendar, MapPin, Edit3, Check, ChevronsUpDown } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../styles/quill-custom.css';

interface Course {
  id: string;
  title: string;
  description: string;
  location: string;
  duration_hours: number;
  max_participants: number;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  position_title: string;
  department: string;
  site: string;
}

interface RosterOptions {
  includeNameTags: boolean;
  includeNameTents: boolean;
  includeSignInSheet: boolean;
  includeAgenda: boolean;
  includeResources: boolean;
  customTitle: string;
  customInstructions: string;
  sessionDate: string;
  sessionTime: string;
  venue: string;
  badgeSize: string;
  tentOrientation: 'landscape' | 'portrait';
}

export default function RosterCreator() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [courseSearchOpen, setCourseSearchOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  
  const [rosterOptions, setRosterOptions] = useState<RosterOptions>({
    includeNameTags: true,
    includeNameTents: false,
    includeSignInSheet: true,
    includeAgenda: false,
    includeResources: false,
    customTitle: '',
    customInstructions: '',
    sessionDate: new Date().toISOString().split('T')[0],
    sessionTime: '09:00',
    venue: '',
    badgeSize: '5160',
    tentOrientation: 'landscape'
  });

  const [generatedContent, setGeneratedContent] = useState({
    roster: '',
    nameTags: '',
    nameTents: '',
    signInSheet: '',
    agenda: '',
    resources: ''
  });

  useEffect(() => {
    fetchCourses();
    fetchUsers();
  }, []);

  const fetchCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('id, title, description, location, duration_hours, max_participants')
        .eq('status', 'published')
        .order('title');
      
      if (error) throw error;
      
      const uniqueCourses = data?.filter((course, index, self) => 
        index === self.findIndex(c => c.title === course.title && c.description === course.description)
      ) || [];
      
      setCourses(uniqueCourses);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch courses",
        variant: "destructive",
      });
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email, position_title, department, site')
        .order('last_name');
      
      if (error) throw error;
      
      // Add mock sample users if no users exist
      const mockUsers = [
        {
          id: 'mock-1',
          first_name: 'Sarah',
          last_name: 'Johnson',
          email: 'sarah.johnson@school.edu',
          position_title: 'Math Teacher',
          department: 'Mathematics',
          site: 'Roosevelt Elementary'
        },
        {
          id: 'mock-2',
          first_name: 'Michael',
          last_name: 'Davis',
          email: 'michael.davis@school.edu',
          position_title: 'Science Teacher',
          department: 'Science',
          site: 'Lincoln Middle School'
        },
        {
          id: 'mock-3',
          first_name: 'Emily',
          last_name: 'Rodriguez',
          email: 'emily.rodriguez@school.edu',
          position_title: 'English Teacher',
          department: 'English',
          site: 'Washington High School'
        },
        {
          id: 'mock-4',
          first_name: 'David',
          last_name: 'Wilson',
          email: 'david.wilson@school.edu',
          position_title: 'Principal',
          department: 'Administration',
          site: 'Roosevelt Elementary'
        },
        {
          id: 'mock-5',
          first_name: 'Lisa',
          last_name: 'Brown',
          email: 'lisa.brown@school.edu',
          position_title: 'Special Ed Teacher',
          department: 'Special Education',
          site: 'Lincoln Middle School'
        }
      ];
      
      const allUsers = [...(data || []), ...mockUsers];
      setUsers(allUsers);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
    }
  };

  const filteredUsers = useMemo(() => 
    users.filter(user => 
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.department?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.position_title?.toLowerCase().includes(searchQuery.toLowerCase())
    ), [users, searchQuery]
  );

  // ReactQuill modules with text alignment options
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'align': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'blockquote'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ]
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'align', 'list', 'bullet', 'link', 'blockquote',
    'color', 'background'
  ];

  const selectedCourseData = courses.find(c => c.id === selectedCourse);
  const selectedUserData = users.filter(u => selectedUsers.includes(u.id));

  const generateRosterContent = () => {
    const courseTitle = rosterOptions.customTitle || selectedCourseData?.title || 'Course Session';
    const sessionDate = rosterOptions.sessionDate ? new Date(rosterOptions.sessionDate).toLocaleDateString() : 'TBD';
    const sessionTime = rosterOptions.sessionTime || 'TBD';
    const venue = rosterOptions.venue || selectedCourseData?.location || 'TBD';
    
    let roster = `# ${courseTitle}\n\n`;
    roster += `**Date:** ${sessionDate}\n`;
    roster += `**Time:** ${sessionTime}\n`;
    roster += `**Venue:** ${venue}\n`;
    roster += `**Duration:** ${selectedCourseData?.duration_hours || 'TBD'} hours\n\n`;
    
    if (rosterOptions.customInstructions) {
      roster += `**Special Instructions:** ${rosterOptions.customInstructions}\n\n`;
    }
    
    roster += `## Participant Roster (${selectedUserData.length} participants)\n\n`;
    roster += '| Name | Email | Job Title | Department | Site |\n';
    
    selectedUserData.forEach(user => {
      roster += `| ${user.first_name} ${user.last_name} | ${user.email} | ${user.position_title || 'N/A'} | ${user.department || 'N/A'} | ${user.site || 'N/A'} |\n`;
    });
    
    return roster;
  };

  const generateSignInSheet = () => {
    const courseTitle = rosterOptions.customTitle || selectedCourseData?.title || 'Course Session';
    const sessionDate = rosterOptions.sessionDate ? new Date(rosterOptions.sessionDate).toLocaleDateString() : 'TBD';
    
    let signIn = `# ${courseTitle} - Sign-in Sheet\n\n`;
    signIn += `**Date:** ${sessionDate}\n`;
    signIn += `**Time:** ${rosterOptions.sessionTime || 'TBD'}\n`;
    signIn += `**Venue:** ${rosterOptions.venue || selectedCourseData?.location || 'TBD'}\n\n`;
    
    signIn += '## Attendance Record\n\n';
    signIn += '| Name | Signature | Time In | Time Out |\n';
    
    selectedUserData.forEach(user => {
      signIn += `| ${user.first_name} ${user.last_name} | _________________________ | _______ | _______ |\n`;
    });
    
    return signIn;
  };

  const generateNameTags = () => {
    const badgeSpecs = {
      '5160': { width: '2.625in', height: '1in', perPage: 30 },
      '5395': { width: '2.333in', height: '3.375in', perPage: 8 },
      '74459': { width: '3in', height: '4in', perPage: 6 },
      '5384': { width: '2.25in', height: '3.5in', perPage: 8 }
    };
    
    const spec = badgeSpecs[rosterOptions.badgeSize as keyof typeof badgeSpecs] || badgeSpecs['5160'];
    
    let html = `<!DOCTYPE html>
<html><head><title>Name Tags</title>
<style>
  @page { size: 8.5in 11in; margin: 0.5in; }
  body { font-family: Arial, sans-serif; margin: 0; }
  .name-tag { 
    width: ${spec.width}; 
    height: ${spec.height}; 
    border: 1px solid #ccc; 
    display: inline-block; 
    text-align: center; 
    vertical-align: top;
    margin: 2px;
    padding: 10px;
    box-sizing: border-box;
    page-break-inside: avoid;
  }
  .name { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
  .title { font-size: 14px; color: #666; }
  .company { font-size: 12px; color: #999; margin-top: 5px; }
</style></head><body>`;
    
    selectedUserData.forEach(user => {
      html += `<div class="name-tag">
        <div class="name">${user.first_name} ${user.last_name}</div>
        <div class="title">${user.position_title || ''}</div>
        <div class="company">${user.department || ''}</div>
      </div>`;
    });
    
    html += '</body></html>';
    return html;
  };

  const generateNameTents = () => {
    const isLandscape = rosterOptions.tentOrientation === 'landscape';
    
    let html = `<!DOCTYPE html>
<html><head><title>Name Tents</title>
<style>
  @page { size: 8.5in 11in; margin: 0.5in; }
  body { font-family: Arial, sans-serif; margin: 0; }
  .name-tent { 
    width: ${isLandscape ? '7.5in' : '5.5in'}; 
    height: ${isLandscape ? '3.5in' : '5in'}; 
    border: 1px solid #ccc; 
    margin: 10px auto;
    text-align: center; 
    display: flex;
    align-items: center;
    justify-content: center;
    page-break-after: always;
    position: relative;
  }
  .name-tent:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    border-top: 1px dashed #999;
  }
  .name { font-size: ${isLandscape ? '36px' : '24px'}; font-weight: bold; }
  .title { font-size: ${isLandscape ? '18px' : '16px'}; color: #666; margin-top: 10px; }
</style></head><body>`;
    
    selectedUserData.forEach(user => {
      html += `<div class="name-tent">
        <div>
          <div class="name">${user.first_name} ${user.last_name}</div>
          <div class="title">${user.position_title || ''}</div>
        </div>
      </div>`;
    });
    
    html += '</body></html>';
    return html;
  };

  const generateWithAI = async () => {
    if (!selectedCourse || selectedUsers.length === 0) {
      toast({
        title: "Missing Information",
        description: "Please select a course and at least one participant",
        variant: "destructive",
      });
      return;
    }

    setGenerating(true);
    try {
      const rosterContent = generateRosterContent();
      let content = { ...generatedContent, roster: rosterContent };

      if (rosterOptions.includeNameTags) {
        content.nameTags = generateNameTags();
      }

      if (rosterOptions.includeNameTents) {
        content.nameTents = generateNameTents();
      }

      if (rosterOptions.includeSignInSheet) {
        content.signInSheet = generateSignInSheet();
      }

      if (rosterOptions.includeAgenda) {
        content.agenda = generateAgenda();
      }

      if (rosterOptions.includeResources) {
        content.resources = generateResources();
      }

      setGeneratedContent(content);
      
      toast({
        title: "Success!",
        description: "Roster and materials generated successfully",
      });
    } catch (error) {
      console.error('Error generating content:', error);
      toast({
        title: "Error",
        description: "Failed to generate roster content",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const generateAgenda = () => {
    const courseTitle = rosterOptions.customTitle || selectedCourseData?.title || 'Course Session';
    const duration = selectedCourseData?.duration_hours || 4;
    
    let agenda = `# ${courseTitle} - Course Agenda\n\n`;
    agenda += `**Total Duration:** ${duration} hours\n\n`;
    agenda += `## Session Outline\n\n`;
    
    const sections = [
      { time: '30 min', title: 'Welcome & Introductions', description: 'Opening remarks and participant introductions' },
      { time: `${Math.floor(duration * 0.4)} hrs`, title: 'Core Content Delivery', description: 'Main learning objectives and content presentation' },
      { time: '15 min', title: 'Break', description: 'Networking and refreshment break' },
      { time: `${Math.floor(duration * 0.3)} hrs`, title: 'Interactive Activities', description: 'Hands-on exercises and group discussions' },
      { time: `${Math.floor(duration * 0.2)} hrs`, title: 'Q&A and Wrap-up', description: 'Questions, feedback, and next steps' }
    ];
    
    agenda += '| Time | Activity | Description |\n';
    
    sections.forEach(section => {
      agenda += `| ${section.time} | ${section.title} | ${section.description} |\n`;
    });
    
    return agenda;
  };

  const generateResources = () => {
    const courseTitle = rosterOptions.customTitle || selectedCourseData?.title || 'Course Session';
    
    let resources = `# ${courseTitle} - Additional Resources\n\n`;
    resources += `## Learning Materials\n\n`;
    resources += `- Course handbook and reference materials\n`;
    resources += `- Online resource portal access\n`;
    resources += `- Supplementary reading list\n\n`;
    resources += `## Tools and Equipment\n\n`;
    resources += `- Required software or applications\n`;
    resources += `- Hardware specifications\n`;
    resources += `- Setup instructions\n\n`;
    resources += `## Support Contacts\n\n`;
    resources += `- Instructor contact information\n`;
    resources += `- Technical support resources\n`;
    resources += `- Help desk and FAQs\n\n`;
    resources += `## Follow-up Resources\n\n`;
    resources += `- Certificate information\n`;
    resources += `- Continuing education opportunities\n`;
    resources += `- Community and networking groups\n`;
    
    return resources;
  };

  const previewContent = (content: string, title: string) => {
    const previewWindow = window.open('', '_blank', 'width=800,height=600');
    if (previewWindow) {
      // Use edited content if currently editing this section
      const contentToPreview = editingSection && generatedContent[editingSection as keyof typeof generatedContent] === content ? editContent : content;
      
      if (contentToPreview.startsWith('<!DOCTYPE html>')) {
        previewWindow.document.write(contentToPreview);
      } else {
        previewWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${title}</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; }
                table { border-collapse: collapse; width: 100%; margin: 20px 0; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f2f2f2; font-weight: bold; }
                h1 { color: #333; border-bottom: 2px solid #007acc; padding-bottom: 10px; }
                h2 { color: #555; margin-top: 30px; }
                h3 { color: #777; }
                strong { color: #333; }
              </style>
            </head>
            <body>
              ${formatContentForDisplay(contentToPreview)}
            </body>
          </html>
        `);
      }
      previewWindow.document.close();
    }
  };

  const downloadContent = (content: string, filename: string) => {
    // Convert HTML content to clean text for download
    const cleanContent = content
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace HTML entities
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\n\s*\n\s*\n/g, '\n\n') // Clean up excessive line breaks
      .trim();

    const blob = new Blob([cleanContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.replace('.html', '.txt'); // Ensure .txt extension
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const printContent = (content: string, title: string = 'Document') => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const formattedContent = formatContentForDisplay(content);
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${title}</title>
            <style>
              @page { 
                size: A4; 
                margin: 1in; 
              }
              body { 
                font-family: Arial, sans-serif; 
                font-size: 12pt; 
                line-height: 1.4;
                color: #000;
                margin: 0;
                padding: 0;
              }
              table { 
                border-collapse: collapse; 
                width: 100%; 
                margin: 20px 0;
                page-break-inside: auto;
              }
              th, td { 
                border: 1px solid #000; 
                padding: 8px; 
                text-align: left;
                font-size: 11pt;
              }
              th { 
                background-color: #f0f0f0 !important; 
                font-weight: bold;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
              h1 { 
                font-size: 18pt; 
                font-weight: bold; 
                margin: 20px 0 15px 0;
                border-bottom: 2px solid #000;
                padding-bottom: 5px;
              }
              h2 { 
                font-size: 14pt; 
                font-weight: bold; 
                margin: 15px 0 10px 0; 
              }
              h3 { 
                font-size: 13pt; 
                font-weight: bold; 
                margin: 12px 0 8px 0; 
              }
              p { 
                margin: 8px 0; 
              }
              strong { 
                font-weight: bold; 
              }
              .overflow-x-auto {
                overflow: visible;
              }
              tr {
                page-break-inside: avoid;
              }
            </style>
          </head>
          <body>
            ${formattedContent}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  const startEditing = (section: string, content: string) => {
    setEditingSection(section);
    setEditContent(content);
  };

  const saveEdit = (section: string) => {
    setGeneratedContent(prev => ({
      ...prev,
      [section]: editContent
    }));
    setEditingSection(null);
    setEditContent('');
    toast({
      title: "Saved",
      description: "Content updated successfully",
    });
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setEditContent('');
  };


  const formatContentForDisplay = (content: string) => {
    // Split content into lines for processing
    const lines = content.split('\n');
    let result = '';
    let inTable = false;
    let isFirstTableRow = true;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Check if this line is a table row (starts and ends with |)
      if (line.trim().match(/^\|.*\|$/)) {
        const cells = line.split('|').slice(1, -1).map(cell => cell.trim());
        
        if (!inTable) {
          // Start new table
          inTable = true;
          isFirstTableRow = true;
          result += '<div class="overflow-x-auto mt-6 mb-6"><table class="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm border border-slate-200">';
        }
        
        if (isFirstTableRow) {
          // First row is header
          result += '<thead class="bg-gradient-to-r from-slate-600 to-slate-700 text-white"><tr>';
          result += cells.map(cell => `<th class="px-6 py-4 text-left font-semibold text-sm border-r border-slate-300 last:border-r-0">${cell}</th>`).join('');
          result += '</tr></thead><tbody>';
          isFirstTableRow = false;
        } else {
          // Data row
          result += '<tr class="hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0">';
          result += cells.map((cell, index) => 
            `<td class="px-6 py-4 text-sm text-slate-700 border-r border-slate-200 last:border-r-0 ${index === 0 ? 'font-medium' : ''}">${cell}</td>`
          ).join('');
          result += '</tr>';
        }
      } else {
        // Not a table row
        if (inTable) {
          // Close current table
          result += '</tbody></table></div>';
          inTable = false;
        }
        
        // Process other content
        let processedLine = line
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        
        if (line.match(/^# /)) {
          processedLine = line.replace(/^# (.*)$/, '<h1 class="text-2xl font-bold text-slate-800 mb-4 border-b-2 border-blue-300 pb-2">$1</h1>');
        } else if (line.match(/^## /)) {
          processedLine = line.replace(/^## (.*)$/, '<h2 class="text-xl font-semibold text-slate-700 mb-3 mt-6">$1</h2>');
        } else if (line.match(/^### /)) {
          processedLine = line.replace(/^### (.*)$/, '<h3 class="text-lg font-medium text-slate-600 mb-2 mt-4">$1</h3>');
        } else if (line.trim() === '') {
          processedLine = '<br>';
        } else if (!line.startsWith('<')) {
          processedLine = `<p class="text-slate-700 leading-relaxed mb-3">${processedLine}</p>`;
        }
        
        result += processedLine;
      }
    }
    
    // Close table if still open
    if (inTable) {
      result += '</tbody></table></div>';
    }
    
    return result;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
          <Wand2 className="h-8 w-8" />
          AI Roster Creator
        </h1>
        <p className="text-muted-foreground">
          Generate customized rosters, name tags, sign-in sheets, and course materials with AI
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Configuration Panel */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Course Selection</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Select Course</Label>
                <Popover open={courseSearchOpen} onOpenChange={setCourseSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={courseSearchOpen}
                      className="w-full justify-between"
                    >
                      {selectedCourse
                        ? courses.find((course) => course.id === selectedCourse)?.title
                        : "Search and select a course..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput placeholder="Search courses..." />
                      <CommandList>
                        <CommandEmpty>No courses found.</CommandEmpty>
                        <CommandGroup>
                          {courses.map((course) => (
                            <CommandItem
                              key={course.id}
                              value={course.title}
                              onSelect={() => {
                                setSelectedCourse(course.id);
                                setCourseSearchOpen(false);
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  selectedCourse === course.id ? "opacity-100" : "opacity-0"
                                }`}
                              />
                              <div>
                                <div className="font-medium">{course.title}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {course.description?.substring(0, 100)}...
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {selectedCourseData && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    {selectedCourseData.description}
                  </p>
                  <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                    <span>{selectedCourseData.duration_hours}h duration</span>
                    <span>Max {selectedCourseData.max_participants} participants</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Session Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Custom Title (Optional)</Label>
                <Input
                  value={rosterOptions.customTitle}
                  onChange={(e) => setRosterOptions({...rosterOptions, customTitle: e.target.value})}
                  placeholder="Override course title..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Session Date</Label>
                  <Input
                    type="date"
                    value={rosterOptions.sessionDate}
                    onChange={(e) => setRosterOptions({...rosterOptions, sessionDate: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Session Time</Label>
                  <Input
                    type="time"
                    value={rosterOptions.sessionTime}
                    onChange={(e) => setRosterOptions({...rosterOptions, sessionTime: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Venue/Location</Label>
                <Input
                  value={rosterOptions.venue}
                  onChange={(e) => setRosterOptions({...rosterOptions, venue: e.target.value})}
                  placeholder="Override course location..."
                />
              </div>

              <div>
                <Label>Custom Instructions</Label>
                <Textarea
                  value={rosterOptions.customInstructions}
                  onChange={(e) => setRosterOptions({...rosterOptions, customInstructions: e.target.value})}
                  placeholder="Special instructions for participants..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Generate Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="nameTags"
                    checked={rosterOptions.includeNameTags}
                    onCheckedChange={(checked) => setRosterOptions({...rosterOptions, includeNameTags: !!checked})}
                  />
                  <Label htmlFor="nameTags">Generate Name Tags</Label>
                </div>
                
                {rosterOptions.includeNameTags && (
                  <div className="ml-6 space-y-2">
                    <Label className="text-xs">Badge Size</Label>
                    <select 
                      value={rosterOptions.badgeSize}
                      onChange={(e) => setRosterOptions({...rosterOptions, badgeSize: e.target.value})}
                      className="w-full p-2 border rounded text-sm"
                    >
                      <option value="5160">Avery 5160 - Address Labels (2.625" x 1")</option>
                      <option value="5395">Avery 5395 - Name Badge Labels (2.333" x 3.375")</option>
                      <option value="74459">Avery 74459 - Name Badge Kit (3" x 4")</option>
                      <option value="5384">Avery 5384 - Name Badges (2.25" x 3.5")</option>
                    </select>
                  </div>
                )}
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="nameTents"
                    checked={rosterOptions.includeNameTents}
                    onCheckedChange={(checked) => setRosterOptions({...rosterOptions, includeNameTents: !!checked})}
                  />
                  <Label htmlFor="nameTents">Generate Name Tents</Label>
                </div>
                
                {rosterOptions.includeNameTents && (
                  <div className="ml-6 space-y-2">
                    <Label className="text-xs">Orientation (8.5" x 11")</Label>
                    <select 
                      value={rosterOptions.tentOrientation}
                      onChange={(e) => setRosterOptions({...rosterOptions, tentOrientation: e.target.value as 'landscape' | 'portrait'})}
                      className="w-full p-2 border rounded text-sm"
                    >
                      <option value="landscape">Landscape</option>
                      <option value="portrait">Portrait</option>
                    </select>
                  </div>
                )}
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="signIn"
                    checked={rosterOptions.includeSignInSheet}
                    onCheckedChange={(checked) => setRosterOptions({...rosterOptions, includeSignInSheet: !!checked})}
                  />
                  <Label htmlFor="signIn">Generate Sign-in Sheet</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="agenda"
                    checked={rosterOptions.includeAgenda}
                    onCheckedChange={(checked) => setRosterOptions({...rosterOptions, includeAgenda: !!checked})}
                  />
                  <Label htmlFor="agenda">AI-Generated Agenda</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="resources"
                    checked={rosterOptions.includeResources}
                    onCheckedChange={(checked) => setRosterOptions({...rosterOptions, includeResources: !!checked})}
                  />
                  <Label htmlFor="resources">AI-Generated Resources</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Participant Selection */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Select Participants ({selectedUsers.length} selected)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="Search users by name, email, department..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              
              <div className="max-h-96 overflow-y-auto space-y-2">
                {filteredUsers.map(user => (
                  <div
                    key={user.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedUsers.includes(user.id) 
                        ? 'bg-primary/10 border-primary' 
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => {
                      if (selectedUsers.includes(user.id)) {
                        setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                      } else {
                        setSelectedUsers([...selectedUsers, user.id]);
                      }
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{user.first_name} {user.last_name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                        <div className="flex gap-2 mt-1">
                          {user.position_title && <Badge variant="outline" className="text-xs">{user.position_title}</Badge>}
                          {user.department && <Badge variant="outline" className="text-xs">{user.department}</Badge>}
                        </div>
                      </div>
                      {selectedUsers.includes(user.id) && (
                        <div className="w-4 h-4 bg-primary rounded-full"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedUsers(filteredUsers.map(u => u.id))}
                >
                  Select All Visible
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedUsers([])}
                >
                  Clear Selection
                </Button>
              </div>
            </CardContent>
          </Card>

          <Button 
            onClick={generateWithAI}
            disabled={!selectedCourse || selectedUsers.length === 0 || generating}
            className="w-full"
            size="lg"
          >
            <Wand2 className="h-4 w-4 mr-2" />
            {generating ? 'Generating...' : 'Generate Roster & Materials'}
          </Button>
        </div>
      </div>

      {/* Generated Content */}
      {generatedContent.roster && (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold">Generated Materials</h2>
          
          <div className="grid gap-6">
            {/* Roster */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Course Roster
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => previewContent(generatedContent.roster, 'Course Roster')}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Preview
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => startEditing('roster', generatedContent.roster)}
                    >
                      <Edit3 className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadContent(generatedContent.roster, 'course-roster.txt')}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => printContent(generatedContent.roster, 'Course Roster')}
                    >
                      <Printer className="h-4 w-4 mr-2" />
                      Print
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                  {editingSection === 'roster' ? (
                    <div className="space-y-4">
                      <ReactQuill
                        value={editContent}
                        onChange={setEditContent}
                        theme="snow"
                        modules={quillModules}
                        formats={quillFormats}
                        className="bg-white"
                        style={{ minHeight: '400px' }}
                      />
                      <div className="flex gap-2 mt-12">
                        <Button size="sm" onClick={() => saveEdit('roster')}>
                          <Check className="h-4 w-4 mr-2" />
                          Save Changes
                        </Button>
                        <Button variant="outline" size="sm" onClick={cancelEdit}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                ) : (
                  <div className="bg-gradient-to-br from-slate-50 to-blue-50 p-6 rounded-lg border border-slate-200">
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ 
                        __html: formatContentForDisplay(generatedContent.roster) 
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Name Tents */}
            {rosterOptions.includeNameTents && generatedContent.nameTents && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Name Tents</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => previewContent(generatedContent.nameTents, 'Name Tents')}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEditing('nameTents', generatedContent.nameTents)}
                      >
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadContent(generatedContent.nameTents, 'name-tents.html')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printContent(generatedContent.nameTents, 'Name Tents')}
                      >
                        <Printer className="h-4 w-4 mr-2" />
                        Print
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {editingSection === 'nameTents' ? (
                    <div className="space-y-4">
                        <ReactQuill
                          value={editContent}
                          onChange={setEditContent}
                          theme="snow"
                          modules={quillModules}
                          formats={quillFormats}
                          className="bg-white"
                          style={{ minHeight: '300px' }}
                        />
                      <div className="flex gap-2 mt-12">
                        <Button size="sm" onClick={() => saveEdit('nameTents')}>
                          <Check className="h-4 w-4 mr-2" />
                          Save Changes
                        </Button>
                        <Button variant="outline" size="sm" onClick={cancelEdit}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-lg border border-purple-200">
                      <div 
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ 
                          __html: generatedContent.nameTents 
                        }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Name Tags */}
            {rosterOptions.includeNameTags && generatedContent.nameTags && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Name Tags</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => previewContent(generatedContent.nameTags, 'Name Tags')}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEditing('nameTags', generatedContent.nameTags)}
                      >
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadContent(generatedContent.nameTags, 'name-tags.html')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printContent(generatedContent.nameTags, 'Name Tags')}
                      >
                        <Printer className="h-4 w-4 mr-2" />
                        Print
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {editingSection === 'nameTags' ? (
                    <div className="space-y-4">
                        <ReactQuill
                          value={editContent}
                          onChange={setEditContent}
                          theme="snow"
                          modules={quillModules}
                          formats={quillFormats}
                          className="bg-white"
                          style={{ minHeight: '300px' }}
                        />
                      <div className="flex gap-2 mt-12">
                        <Button size="sm" onClick={() => saveEdit('nameTags')}>
                          <Check className="h-4 w-4 mr-2" />
                          Save Changes
                        </Button>
                        <Button variant="outline" size="sm" onClick={cancelEdit}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
                      <div 
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ 
                          __html: generatedContent.nameTags 
                        }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Sign-in Sheet */}
            {rosterOptions.includeSignInSheet && generatedContent.signInSheet && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Sign-in Sheet</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => previewContent(generatedContent.signInSheet, 'Sign-in Sheet')}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEditing('signInSheet', generatedContent.signInSheet)}
                      >
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadContent(generatedContent.signInSheet, 'sign-in-sheet.txt')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printContent(generatedContent.signInSheet, 'Sign-in Sheet')}
                      >
                        <Printer className="h-4 w-4 mr-2" />
                        Print
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {editingSection === 'signInSheet' ? (
                    <div className="space-y-4">
                      <ReactQuill
                        value={editContent}
                        onChange={setEditContent}
                        theme="snow"
                        modules={quillModules}
                        formats={quillFormats}
                        className="bg-white"
                        style={{ minHeight: '300px' }}
                      />
                      <div className="flex gap-2 mt-12">
                        <Button size="sm" onClick={() => saveEdit('signInSheet')}>
                          <Check className="h-4 w-4 mr-2" />
                          Save Changes
                        </Button>
                        <Button variant="outline" size="sm" onClick={cancelEdit}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-lg border border-amber-200">
                      <div 
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ 
                          __html: formatContentForDisplay(generatedContent.signInSheet) 
                        }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* AI-Generated Agenda */}
            {rosterOptions.includeAgenda && generatedContent.agenda && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="flex items-center gap-2">
                      <Wand2 className="h-4 w-4" />
                      AI-Generated Agenda
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => previewContent(generatedContent.agenda, 'AI-Generated Agenda')}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEditing('agenda', generatedContent.agenda)}
                      >
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadContent(generatedContent.agenda, 'course-agenda.txt')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printContent(generatedContent.agenda, 'Course Agenda')}
                      >
                        <Printer className="h-4 w-4 mr-2" />
                        Print
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {editingSection === 'agenda' ? (
                    <div className="space-y-4">
                      <ReactQuill
                        value={editContent}
                        onChange={setEditContent}
                        theme="snow"
                        modules={quillModules}
                        formats={quillFormats}
                        className="bg-white"
                        style={{ minHeight: '300px' }}
                      />
                      <div className="flex gap-2 mt-12">
                        <Button size="sm" onClick={() => saveEdit('agenda')}>
                          <Check className="h-4 w-4 mr-2" />
                          Save Changes
                        </Button>
                        <Button variant="outline" size="sm" onClick={cancelEdit}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-lg border border-purple-200">
                      <div 
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ 
                          __html: formatContentForDisplay(generatedContent.agenda) 
                        }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* AI-Generated Resources */}
            {rosterOptions.includeResources && generatedContent.resources && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="flex items-center gap-2">
                      <Wand2 className="h-4 w-4" />
                      AI-Generated Resources
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => previewContent(generatedContent.resources, 'AI-Generated Resources')}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEditing('resources', generatedContent.resources)}
                      >
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadContent(generatedContent.resources, 'course-resources.txt')}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printContent(generatedContent.resources, 'Course Resources')}
                      >
                        <Printer className="h-4 w-4 mr-2" />
                        Print
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {editingSection === 'resources' ? (
                    <div className="space-y-4">
                      <ReactQuill
                        value={editContent}
                        onChange={setEditContent}
                        theme="snow"
                        modules={quillModules}
                        formats={quillFormats}
                        className="bg-white"
                        style={{ minHeight: '300px' }}
                      />
                      <div className="flex gap-2 mt-12">
                        <Button size="sm" onClick={() => saveEdit('resources')}>
                          <Check className="h-4 w-4 mr-2" />
                          Save Changes
                        </Button>
                        <Button variant="outline" size="sm" onClick={cancelEdit}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-lg border border-teal-200">
                      <div 
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ 
                          __html: formatContentForDisplay(generatedContent.resources) 
                        }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}